import { FormGroup, FormControlLabel, Checkbox, Typography, Box, Tooltip } from '@mui/material';
import type { SyncPreferences } from './types';
import type { SyncCapabilitySet } from '../viewer/types';

const SYNC_ITEMS: { key: keyof SyncPreferences; label: string }[] = [
  { key: 'scroll', label: 'Scroll / Slice position' },
  { key: 'windowLevel', label: 'Window / Level' },
  { key: 'tool', label: 'Active tool' },
  { key: 'crosshair', label: 'Crosshair position' },
  { key: 'annotations', label: 'Annotations / Measurements' },
];

interface SyncOptionsPanelProps {
  preferences: SyncPreferences;
  onChange: <K extends keyof SyncPreferences>(key: K, value: boolean) => void;
  /** Capabilities declared by the active viewer. When absent, all are enabled. */
  capabilities?: SyncCapabilitySet;
}

export function SyncOptionsPanel({ preferences, onChange, capabilities }: SyncOptionsPanelProps) {
  const hasAnySync = capabilities
    ? Object.values(capabilities).some(Boolean)
    : true;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Synchronization
      </Typography>
      {!hasAnySync && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          This viewer does not support cross-window synchronization.
          Window placement and study routing still work.
        </Typography>
      )}
      <FormGroup>
        {SYNC_ITEMS.map(({ key, label }) => {
          const supported = capabilities ? capabilities[key] : true;
          return (
            <Tooltip
              key={key}
              title={supported ? '' : 'Not supported by this viewer'}
              placement="right"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={supported && preferences[key]}
                    disabled={!supported}
                    onChange={(_, checked) => onChange(key, checked)}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    color={supported ? 'text.primary' : 'text.disabled'}
                  >
                    {label}
                  </Typography>
                }
              />
            </Tooltip>
          );
        })}
      </FormGroup>
    </Box>
  );
}
